import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Col, Form, Row } from 'react-bootstrap';
import styled from 'styled-components';

const formatadorMoeda = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const MENSAGEM_ACIMA_DO_TETO = 'Valor acima das faixas da VX — contate a gerenciadora de risco.';

export const formatarMoeda = valor => (valor === null || valor === undefined || valor === '' ? '' : formatadorMoeda.format(Number(valor)));

// Máscara de moeda: os dígitos digitados são centavos (3500000 → 35.000,00).
const lerMoeda = texto => {
  const digitos = String(texto || '').replace(/\D/g, '').slice(0, 13);
  return digitos ? Number(digitos) / 100 : null;
};

export const faixaDoValor = (faixas, valor) => (valor === null || valor === undefined
  ? null
  : (faixas || []).find(faixa => valor >= Number(faixa.minimo) && valor <= Number(faixa.maximo)) || null);

export const acimaDoTeto = (faixas, valor) => {
  if (valor === null || valor === undefined || !(faixas || []).length) return false;
  return valor > Math.max(...faixas.map(faixa => Number(faixa.maximo)));
};

/** Quantidade de itens pendentes da carga: tipo de mercadoria e faixa de valor (dentro do teto). */
export const pendenciasCarga = (valores, faixas) => [
  !valores?.tipo_mercadoria,
  !valores?.valor || acimaDoTeto(faixas, valores?.valor_carga)
].filter(Boolean).length;

export const descreverCarga = (valores, opcoes) => {
  const tipo = (opcoes?.tipos_carga || []).find(item => Number(item.id) === Number(valores?.tipo_mercadoria));
  const faixa = (opcoes?.faixas_valor || []).find(item => Number(item.id) === Number(valores?.valor));
  return {
    mercadoria: tipo?.titulo || 'Não informada',
    faixa: faixa?.descricao || 'Não informada',
    valorInformado: valores?.valor_carga ? `R$ ${formatarMoeda(valores.valor_carga)}` : ''
  };
};

const VxCargaCampos = ({ opcoes, carregando, erro, valores, onChange, disabled }) => {
  const faixas = opcoes?.faixas_valor || [];

  // Alguns títulos se repetem na VX com IDs diferentes ("Refrigerante" 90 e 102): mostra o ID para diferenciar.
  const opcoesTipo = useMemo(() => {
    const tipos = opcoes?.tipos_carga || [];
    const repetidos = new Set(tipos.map(tipo => tipo.titulo.toUpperCase()).filter((titulo, indice, lista) => lista.indexOf(titulo) !== indice));
    return tipos.map(tipo => ({
      value: Number(tipo.id),
      label: repetidos.has(tipo.titulo.toUpperCase()) ? `${tipo.titulo} (${tipo.id})` : tipo.titulo
    }));
  }, [opcoes]);

  const tipoSelecionado = opcoesTipo.find(opcao => opcao.value === Number(valores?.tipo_mercadoria)) || null;
  const valorCarga = valores?.valor_carga ?? null;
  const ultrapassou = acimaDoTeto(faixas, valorCarga);

  const alterarValorCarga = event => {
    const valor = lerMoeda(event.target.value);
    const faixa = acimaDoTeto(faixas, valor) ? null : faixaDoValor(faixas, valor);
    onChange({ ...valores, valor_carga: valor, valor: faixa ? Number(faixa.id) : null });
  };

  // Escolher a faixa direto limpa o valor digitado, para não ficar "R$ 350 mil" ao lado de outra faixa.
  const alterarFaixa = event => onChange({ ...valores, valor: event.target.value ? Number(event.target.value) : null, valor_carga: null });

  if (erro) return <div className="alert alert-danger py-2 mb-0">{erro}</div>;

  return (
    <Row>
      <Col md={12}>
        <Form.Group className="mb-2">
          <Rotulo htmlFor="vx-tipo-mercadoria">Tipo de mercadoria *</Rotulo>
          <Select
            inputId="vx-tipo-mercadoria"
            classNamePrefix="vx-select"
            options={opcoesTipo}
            value={tipoSelecionado}
            onChange={opcao => onChange({ ...valores, tipo_mercadoria: opcao ? opcao.value : null })}
            isLoading={carregando}
            isDisabled={disabled || carregando}
            isClearable
            placeholder={carregando ? 'Carregando tipos da VX...' : 'Busque o tipo de mercadoria...'}
            noOptionsMessage={() => 'Nenhum tipo encontrado.'}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 2000 }),
              control: base => ({ ...base, minHeight: 31, fontSize: 14, borderColor: tipoSelecionado ? base.borderColor : '#dc3545' })
            }}
          />
        </Form.Group>
      </Col>
      <Col md={5}>
        <Form.Group className="mb-2" controlId="vx-valor-carga">
          <Rotulo>Valor da carga (R$)</Rotulo>
          <Form.Control
            size="sm"
            inputMode="numeric"
            placeholder="0,00"
            value={formatarMoeda(valorCarga)}
            onChange={alterarValorCarga}
            disabled={disabled}
            isInvalid={ultrapassou}
          />
          <Form.Control.Feedback type="invalid">{MENSAGEM_ACIMA_DO_TETO}</Form.Control.Feedback>
          {!ultrapassou ? <Form.Text muted>Digite o valor e a faixa é escolhida automaticamente.</Form.Text> : null}
        </Form.Group>
      </Col>
      <Col md={7}>
        <Form.Group className="mb-2" controlId="vx-faixa-valor">
          <Rotulo>Faixa de valor *</Rotulo>
          <Form.Select size="sm" value={valores?.valor ?? ''} onChange={alterarFaixa} disabled={disabled || carregando} isInvalid={!valores?.valor}>
            <option value="">Selecione a faixa...</option>
            {faixas.map(faixa => <option key={faixa.id} value={faixa.id}>{faixa.descricao}</option>)}
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  );
};

VxCargaCampos.propTypes = {
  opcoes: PropTypes.shape({ tipos_carga: PropTypes.arrayOf(PropTypes.shape()), faixas_valor: PropTypes.arrayOf(PropTypes.shape()) }),
  carregando: PropTypes.bool,
  erro: PropTypes.string,
  valores: PropTypes.shape({ tipo_mercadoria: PropTypes.number, valor: PropTypes.number, valor_carga: PropTypes.number }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

VxCargaCampos.defaultProps = {
  opcoes: null,
  carregando: false,
  erro: '',
  valores: {},
  disabled: false
};

export default VxCargaCampos;

const Rotulo = styled(Form.Label)`
  margin-bottom: 2px;
  color: #495057;
  font-size: 12px;
  font-weight: 600;
`;
